import { useState } from "react";
import {
    TableRow,
    TableCell,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead,
    TableBody,
    Typography,
} from "@mui/material";
import { deepPurple } from "@mui/material/colors";
import Iconify from "./iconify";
import { getPositionLetter, isTeamPosition, getPlayerPositionFromLineupPosition } from "../utils/positionUtils";

const STAT_LABELS = {
    // Hockey
    goal: "Goals",
    assist: "Assists",
    shotOnTarget: "Shots on Target",
    hockeyHit: "Hits",
    hockeyBlockedShot: "Blocked Shots",
    teamWin: "Team Win",
    hockeyTeamPowerPlayGoal: "PP Goals",
    hockeyTeamShortHandedGoal: "SH Goals",
    teamGoalAgainst: "Goals Against",
    teamCleanSheet: "Clean Sheet",
    teamSave: "Saves",
    // Football/Soccer
    tackleWon: "Tackles Won",
    cleanSheet: "Clean Sheet",
    passIntercepted: "Pass Interceptions",
    teamGoal: "Team Goals",
    teamCorner: "Corners",
    matchPlayed: "Match Played",
    // Basketball
    basketballPoints: "Points",
    basketballAssists: "Assists",
    basketballRebounds: "Rebounds",
    basketballThreePointers: "3-Pointers",
    basketballSteals: "Steals",
    basketballBlocks: "Blocks",
    basketballTurnovers: "Turnovers",
    basketballTeamWin: "Team Win",
    basketballTeamThreePointers: "Team 3-Pointers",
    basketballTeamHundredPoints: "Team 100+ Pts",
    basketballTeamOpponentUnderHundredPoints: "Opp Under 100 Pts",
    basketballTeamOpponentTurnovers: "Opp Turnovers",
    basketballTeamBlocks: "Team Blocks",
    // Australian Football
    australianFootballKicks: "Kicks",
    australianFootballHandballs: "Handballs",
    australianFootballTackles: "Tackles",
    australianFootballHitouts: "Hitouts",
    australianFootballGoals: "Goals",
    australianFootballFreeKicksAgainst: "Free Kicks Against",
    australianFootballTeamWin: "Team Win",
    australianFootballTeamGoals: "Team Goals",
    australianFootballTeamTackles: "Team Tackles",
    australianFootballTeamMarks: "Team Marks",
    australianFootballTeamDefensiveLockdown: "Defensive Lockdown",
    // Rugby
    rugbyTries: "Tries",
    rugbyTryAssists: "Try Assists",
    rugbyLineBreaks: "Line Breaks",
    rugbyTackles: "Tackles",
    rugbyTackleBreaks: "Tackle Breaks",
    rugbyGoals: "Goals",
    rugbyTeamWin: "Team Win",
    rugbyTeamTries: "Team Tries",
    rugbyTeamTackles: "Team Tackles",
    rugbyTeamLineBreaks: "Team Line Breaks",
    rugbyTeamDefensiveLockdown: "Defensive Lockdown",
    // Cricket
    cricketRuns: "Runs",
    cricketFours: "Fours",
    cricketSixes: "Sixes",
    cricketFiftyRuns: "50+ Runs",
    cricketWickets: "Wickets",
    cricketThreeWickets: "3+ Wickets",
    cricketTeamWin: "Team Win",
    cricketTeamRuns: "Team Runs",
    cricketTeamWickets: "Team Wickets",
    cricketTeamSixes: "Team Sixes",
    cricketTeamDefensiveLockdown: "Defensive Lockdown",
};

const getStatLabel = (name) => STAT_LABELS[name] || name;

export default function LineupTableRow({
    row,
    rowIndex,
    cards,
    selectedCardIds,
    handleChangeCard,
    deleteSlot,
    isViewer,
}) {
    const [breakdownOpen, setBreakdownOpen] = useState(false);

    const isCardSelected = (cardId) => {
        return selectedCardIds.includes(cardId) && cardId !== row.cardId;
    };

    // Get the player position for this lineup position
    const playerPositionForFiltering = getPlayerPositionFromLineupPosition(row.playerPosition);

    return (
        <TableRow
            sx={{
                "&:last-child td, &:last-child th": { border: 0 },
            }}
        >
            <TableCell component="th" scope="row" sx={{ textAlign: "center" }}>
                {row.score}
            </TableCell>
            <TableCell component="th" scope="row" sx={{ textAlign: "center" }}>
                {row.name}
            </TableCell>
            <TableCell component="th" scope="row" sx={{ textAlign: "center" }}>
                {row.team === null ? "--" : row.team}
            </TableCell>
            <TableCell component="th" scope="row" sx={{ display: "flex", justifyContent: "center" }}>
                <FormControl>
                    <InputLabel id="status-select-label">Card</InputLabel>
                    <Select
                        labelId="card-label"
                        id="card-select"
                        value={row.cardId}
                        style={{ width: 450 }}
                        label="Card"
                        onChange={(event) => handleChangeCard(event, rowIndex)}
                    >
                        {cards !== null &&
                            !isTeamPosition(row.playerPosition) &&
                            cards
                                .filter((item) => item.position === playerPositionForFiltering)
                                .map((card, index) => {
                                    const isDisabled = isCardSelected(card.cardId);
                                    return (
                                        <MenuItem
                                            key={index}
                                            value={card.cardId}
                                            disabled={isDisabled}
                                            sx={isDisabled ? { opacity: 0.5, backgroundColor: '#f0f0f0' } : {}}
                                        >
                                            <Avatar
                                                sx={{
                                                    bgcolor: deepPurple[500],
                                                    width: 24,
                                                    height: 24,
                                                    fontSize: 10,
                                                    mr: 1,
                                                    float: "left",
                                                }}
                                            >
                                                {getPositionLetter(card.playerPosition)}
                                            </Avatar>
                                            {card.firstname} {card.lastname}
                                        </MenuItem>
                                    );
                                })}
                        {cards !== null &&
                            isTeamPosition(row.playerPosition) &&
                            cards
                                .filter((item) => item.position === 0)
                                .map((card, index) => {
                                    const isDisabled = isCardSelected(card.cardId);
                                    return (
                                        <MenuItem
                                            key={index}
                                            value={card.cardId}
                                            disabled={isDisabled}
                                            sx={isDisabled ? { opacity: 0.5, backgroundColor: '#f0f0f0' } : {}}
                                        >
                                            <Avatar
                                                sx={{
                                                    bgcolor: deepPurple[500],
                                                    width: 24,
                                                    height: 24,
                                                    fontSize: 10,
                                                    mr: 1,
                                                    float: "left",
                                                }}
                                            >
                                                {getPositionLetter(card.playerPosition)}
                                            </Avatar>
                                            {card.firstname} {card.lastname}
                                        </MenuItem>
                                    );
                                })}
                    </Select>
                </FormControl>
            </TableCell>
            {/* <TableCell component="th" scope="row">
                <FormControl fullWidth>
                    <InputLabel id="status-select-label">Modifier</InputLabel>
                    <Select
                        labelId="modifier-label"
                        id="modifier-select"
                        value={row.modifier}
                        style={{ width: 150 }}
                        label="Modifier"
                        onChange={(event) => handleChangeModifier(event, row.modifier)}
                    >
                        {modifiers.map((modifier, index) => (
                            <MenuItem key={index} value={modifier.modifier}>
                                {modifier.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </TableCell> */}
            {!isViewer && (
                <TableCell component="th" scope="row">
                    <Button
                        variant="contained"
                        startIcon={<Iconify icon="eva:trash-2-outline" />}
                        onClick={() => deleteSlot(row.lineupSlotId)}
                    >
                        Delete
                    </Button>
                </TableCell>
            )}
            <TableCell component="th" scope="row">
                <Button
                    variant="outlined"
                    size="small"
                    disabled={!row.scoreBreakdown || row.scoreBreakdown.length === 0}
                    onClick={() => setBreakdownOpen(true)}
                >
                    Breakdown
                </Button>
                <Dialog
                    open={breakdownOpen}
                    onClose={() => setBreakdownOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        Points Breakdown — {row.name}
                    </DialogTitle>
                    <DialogContent>
                        {row.scoreBreakdown && row.scoreBreakdown.length > 0 ? (
                            <Table size="small" sx={{ mt: 1 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Stat</TableCell>
                                        <TableCell align="center">Value</TableCell>
                                        <TableCell align="center">Points per</TableCell>
                                        <TableCell align="center">Training Boost</TableCell>
                                        <TableCell align="center">Total Earned</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {row.scoreBreakdown.map((item, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{getStatLabel(item.name)}</TableCell>
                                            <TableCell align="center">{item.value}</TableCell>
                                            <TableCell align="center">{item.points}</TableCell>
                                            <TableCell align="center">{item.trainingBoostPoints}</TableCell>
                                            <TableCell align="center">
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontWeight: "bold",
                                                        color: item.totalEarnedPoints > 0
                                                            ? "success.main"
                                                            : item.totalEarnedPoints < 0
                                                                ? "error.main"
                                                                : "text.secondary",
                                                    }}
                                                >
                                                    {item.totalEarnedPoints}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ fontWeight: "bold" }}>Total</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                            {row.scoreBreakdown.reduce((sum, item) => sum + (item.totalEarnedPoints || 0), 0)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                No breakdown data available.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBreakdownOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </TableCell>
        </TableRow>
    );
}
